/**
 * 表示用の小さな Swift トークナイザ（CodeBlock と CodeEditor で共有）。
 * クラス名は code.css（Chroma 互換）に合わせる。HTML は作らない。行ごとのトークン列を返し、
 * 描画側が <span class> を組み立てる（{@html} 不使用）。厳密な構文解析はしない。
 *
 * 行をまたぐのはブロックコメント（入れ子可）と """ 文字列だけ。行頭の状態を持ち回し、
 * 「状態 + 行の文字列」が同じなら同じ結果になる純関数なので、行単位でキャッシュできる。
 */

export interface Token {
  text: string;
  /** code.css のクラス。null は地の文字 */
  cls: string | null;
}

/** 行頭の状態。comment はブロックコメントの入れ子の深さ、string は """ の中か */
export interface LineState {
  comment: number;
  string: boolean;
}

export const INITIAL_STATE: LineState = Object.freeze({ comment: 0, string: false });

const KEYWORDS = new Set([
  'let',
  'var',
  'func',
  'return',
  'if',
  'else',
  'guard',
  'for',
  'in',
  'while',
  'repeat',
  'switch',
  'case',
  'default',
  'break',
  'continue',
  'struct',
  'class',
  'enum',
  'protocol',
  'extension',
  'import',
  'init',
  'deinit',
  'subscript',
  'self',
  'Self',
  'inout',
  'where',
  'throws',
  'rethrows',
  'throw',
  'try',
  'do',
  'catch',
  'defer',
  'mutating',
  'nonmutating',
  'static',
  'private',
  'fileprivate',
  'public',
  'internal',
  'open',
  'final',
  'as',
  'is',
  'some',
  'any',
  'async',
  'await',
  'typealias',
  'associatedtype',
  'fallthrough',
  'lazy',
  'weak',
  'unowned',
  'override',
  'convenience',
  'required',
  'operator',
  'indirect',
  'actor',
  'super',
]);
const CONSTANTS = new Set(['true', 'false', 'nil']);
const BUILTINS = new Set([
  'print',
  'readLine',
  'min',
  'max',
  'abs',
  'stride',
  'zip',
  'swap',
  'debugPrint',
  'fatalError',
  'precondition',
  'assert',
  'type',
]);

type Rule = [RegExp, string | null];

// sticky（y）で lastIndex から照合する。rest.slice を繰り返さないので長い行でも線形
const RULES: Rule[] = [
  [/\/\/.*/y, 'c1'],
  [/"(?:[^"\\]|\\.)*"?/y, 's'],
  [/0x[0-9a-fA-F_]+/y, 'mh'],
  [/0b[01_]+/y, 'mb'],
  [/0o[0-7_]+/y, 'mo'],
  [/[0-9][0-9_]*(?:\.[0-9][0-9_]*)?(?:[eE][+-]?[0-9]+)?/y, 'num'],
  [/\$[0-9A-Za-z_]+/y, 'nv'],
  [/#[A-Za-z_][A-Za-z0-9_]*/y, 'cp'],
  [/@[A-Za-z_][A-Za-z0-9_]*/y, 'na'],
  [/`[^`]*`/y, 'nv'],
  [/[\p{L}_][\p{L}\p{N}_]*/uy, 'ident'],
  [/[+\-*/%=<>!&|^~?.:]+/y, 'o'],
  [/[()[\]{},;]/y, 'p'],
  [/\s+/y, null],
];

function classifyIdent(word: string, next: string): string {
  if (KEYWORDS.has(word)) return 'k';
  if (CONSTANTS.has(word)) return 'kc';
  if (BUILTINS.has(word) && next === '(') return 'nb';
  if (/^[A-Z]/.test(word)) return 'kt';
  if (next === '(') return 'nf';
  return 'nv';
}

function push(out: Token[], text: string, cls: string | null) {
  if (text === '') return;
  const prev = out[out.length - 1];
  if (prev && prev.cls === cls) prev.text += text;
  else out.push({ text, cls });
}

/** ブロックコメントの中を進める。閉じた位置（または行末）と残りの深さを返す */
function scanComment(line: string, from: number, depth: number): [number, number] {
  let i = from;
  while (i < line.length && depth > 0) {
    if (line.startsWith('/*', i)) {
      depth++;
      i += 2;
    } else if (line.startsWith('*/', i)) {
      depth--;
      i += 2;
    } else i++;
  }
  return [i, depth];
}

/** 1 行を色付けする。行頭の状態を受け取り、行末の状態を返す */
export function tokenizeLine(
  line: string,
  state: LineState = INITIAL_STATE,
): { tokens: Token[]; end: LineState } {
  const out: Token[] = [];
  let i = 0;
  let comment = state.comment;
  let inString = state.string;

  while (i < line.length) {
    if (comment > 0) {
      const [j, d] = scanComment(line, i, comment);
      push(out, line.slice(i, j), 'cm');
      i = j;
      comment = d;
      continue;
    }
    if (inString) {
      const close = line.indexOf('"""', i);
      const j = close < 0 ? line.length : close + 3;
      push(out, line.slice(i, j), 's');
      i = j;
      inString = close < 0;
      continue;
    }
    if (line.startsWith('/*', i)) {
      const [j, d] = scanComment(line, i + 2, 1);
      push(out, line.slice(i, j), 'cm');
      i = j;
      comment = d;
      continue;
    }
    if (line.startsWith('"""', i)) {
      const close = line.indexOf('"""', i + 3);
      const j = close < 0 ? line.length : close + 3;
      push(out, line.slice(i, j), 's');
      i = j;
      inString = close < 0;
      continue;
    }

    let matched = false;
    for (const [re, cls] of RULES) {
      re.lastIndex = i;
      const m = re.exec(line);
      if (!m || m[0] === '') continue;
      const text = m[0];
      i += text.length;
      let finalCls = cls;
      if (cls === 'ident') finalCls = classifyIdent(text, line.charAt(i));
      else if (cls === 'num') finalCls = /[.eE]/.test(text) ? 'mf' : 'mi';
      push(out, text, finalCls);
      matched = true;
      break;
    }
    if (!matched) {
      push(out, line.charAt(i), null);
      i++;
    }
  }
  return { tokens: out, end: { comment, string: inString } };
}

/** 行頭の状態なしで 1 行だけ色付けする（ログの 1 行表示など） */
export function highlightLine(line: string): Token[] {
  return tokenizeLine(line).tokens;
}

function splitLines(code: string): string[] {
  return code.replace(/\r\n/g, '\n').split('\n');
}

/** コード全体を行に分けて色付けする。末尾の改行 1 つは行として数えない（表示用） */
export function highlightSwift(code: string): Token[][] {
  return tokenizeLines(splitLines(code.replace(/\r?\n$/, '')));
}

function tokenizeLines(lines: string[]): Token[][] {
  let state = INITIAL_STATE;
  return lines.map((l) => {
    const r = tokenizeLine(l, state);
    state = r.end;
    return r.tokens;
  });
}

/**
 * エディタ用: 改行で割った行をそのまま色付けする（末尾の空行も 1 行として返す。textarea と行数を揃える）。
 * 「行頭の状態 + 行」をキーに前回の結果を使い回すので、1 行だけ変えたときは
 * その行（と状態が変わった後続行）だけを再計算し、他の行は同じ配列を返す（描画側の差分更新が効く）。
 */
export function createLineHighlighter(maxEntries = 4000): (code: string) => Token[][] {
  let cache = new Map<string, { tokens: Token[]; end: LineState }>();
  return (code: string) => {
    const next = new Map<string, { tokens: Token[]; end: LineState }>();
    let state = INITIAL_STATE;
    const result = splitLines(code).map((line) => {
      const key = `${state.comment}|${state.string ? 1 : 0}|${line}`;
      const hit = cache.get(key) ?? next.get(key) ?? tokenizeLine(line, state);
      next.set(key, hit);
      state = hit.end;
      return hit.tokens;
    });
    // 今回使った行だけを残す（貼り付けを繰り返しても増え続けない）
    cache = next.size <= maxEntries ? next : new Map();
    return result;
  };
}
